import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CountryShort, MainInterestShort, RegionShort, SpecializationShort } from "../../../Models";

interface AuthSliceState {
    countries: CountryShort[];
    regionsByCountry: { [name: string]: RegionShort[] };
    specializations: SpecializationShort[];
    mainInterests: MainInterestShort[];
    loginFormData: {
        email: string;
        password: string;
        keepMeLoggedIn: boolean;
    };
    loginPageIsLoading: boolean;
    loginPageError: string | undefined;
    updatePasswordPageIsLoading: boolean;
    updatePasswordPageError: string | undefined;
    updatePasswordApproved: boolean;
    loginPageMailApproved: boolean;
    emailApprovementInProgress: boolean;
    registerFormData: {
        fullName: string;
        country: string;
        region: string | undefined;
        email: string;
        mainInterest: string;
        expertise: string;
        password: string;
        confirmPassword: string;
        acceptTerms: boolean;
    };
    registerPageIsLoading: boolean;
    successfullyRegisteredForEmail: string | undefined;
    registrationError: string | undefined;
    emailResendInProgress: boolean;
}

const initialAuthState: AuthSliceState = {
    countries: [],
    regionsByCountry: {},
    specializations: [],
    mainInterests: [],
    loginFormData: {
        email: "",
        password: "",
        keepMeLoggedIn: false,
    },
    loginPageIsLoading: false,
    loginPageError: undefined,
    updatePasswordPageIsLoading: false,
    updatePasswordPageError: undefined,
    updatePasswordApproved: false,
    loginPageMailApproved: false,
    emailApprovementInProgress: false,
    registerFormData: {
        fullName: "",
        country: "",
        region: undefined,
        email: "",
        mainInterest: "",
        expertise: "",
        password: "",
        confirmPassword: "",
        acceptTerms: true,
    },
    registerPageIsLoading: false,
    successfullyRegisteredForEmail: undefined,
    registrationError: undefined,
    emailResendInProgress: false,
};

export const authSlice = createSlice({
    name: "auth",
    initialState: initialAuthState,
    reducers: {
        setRegistrationData: (
            state: AuthSliceState,
            action: PayloadAction<{
                countries: CountryShort[];
                regionsByCountry: { [name: string]: RegionShort[] };
                specializations: SpecializationShort[];
                mainInterests: MainInterestShort[];
            }>
        ) => {
            const { countries, regionsByCountry, specializations, mainInterests } = action.payload;

            state.countries = countries;
            state.regionsByCountry = regionsByCountry;
            state.specializations = specializations;
            state.mainInterests = mainInterests;
        },
        setRegisterFormData: (
            state: AuthSliceState,
            action: PayloadAction<{
                fullName: string;
                country: string;
                region: string | undefined;
                email: string;
                mainInterest: string;
                expertise: string;
                password: string;
                confirmPassword: string;
                acceptTerms: boolean;
            }>
        ) => {
            const { fullName, country, region, email, mainInterest, expertise, password, confirmPassword, acceptTerms } = action.payload;
            state.registerFormData = {
                fullName: fullName,
                country: country,
                region: region,
                email: email,
                mainInterest: mainInterest,
                expertise: expertise,
                password: password,
                confirmPassword: confirmPassword,
                acceptTerms: acceptTerms,
            };
        },
        setLoginFormData: (
            state: AuthSliceState,
            action: PayloadAction<{
                email: string;
                password: string;
                keepMeLoggedIn: boolean;
            }>
        ) => {
            const { email, password, keepMeLoggedIn } = action.payload;
            state.loginFormData = {
                email: email,
                password: password,
                keepMeLoggedIn: keepMeLoggedIn,
            };
        },
        setRegistrationInProgress: (state: AuthSliceState) => {
            state.registerPageIsLoading = true;
            state.successfullyRegisteredForEmail = undefined;
            state.registrationError = undefined;
        },
        setRegistrationCompleted: (state: AuthSliceState, action: PayloadAction<string>) => {
            state.registerPageIsLoading = false;
            state.registrationError = undefined;
            state.successfullyRegisteredForEmail = action.payload;
        },
        setRegistrationFailed: (state: AuthSliceState, action: PayloadAction<string>) => {
            state.registerPageIsLoading = false;
            state.registrationError = action.payload;
            state.successfullyRegisteredForEmail = undefined;
        },
        setLoginInProgress: (state: AuthSliceState) => {
            state.loginPageIsLoading = true;
            state.loginPageError = undefined;
        },
        setLoginFailed: (state: AuthSliceState, action: PayloadAction<string>) => {
            console.log("setLoginFailed", action.payload);
            state.loginPageIsLoading = false;
            state.loginPageError = action.payload;
        },
        setCountry: (state: AuthSliceState, action: PayloadAction<string>) => {
            state.registerFormData.country = action.payload;
        },
        setMainInterest: (state: AuthSliceState, action: PayloadAction<string>) => {
            state.registerFormData.mainInterest = action.payload;
        },
        setExpertise: (state: AuthSliceState, action: PayloadAction<string>) => {
            state.registerFormData.expertise = action.payload;
        },
        setLoginCompleted: (state: AuthSliceState) => {
            state.loginPageIsLoading = false;
            state.loginPageError = undefined;
        },
        setEmailConfirmationInProgress: (state: AuthSliceState) => {
            state.emailApprovementInProgress = true;
            state.loginPageMailApproved = false;
        },
        setEmailConfirmationSuccess: (state: AuthSliceState) => {
            state.emailApprovementInProgress = false;
            state.loginPageMailApproved = true;
            state.loginPageError = undefined;
        },
        setEmailConfirmationError: (state: AuthSliceState, action: PayloadAction<string>) => {
            state.emailApprovementInProgress = false;
            state.loginPageMailApproved = false;
            state.loginPageError = action.payload;
        },
        setEmailResendInProgress: (state: AuthSliceState, action: PayloadAction<boolean>) => {
            state.emailResendInProgress = action.payload;
        },
        setUpdatePasswordInProgress: (state: AuthSliceState) => {
            state.updatePasswordPageIsLoading = true;
            state.updatePasswordPageError = undefined;
        },
        setUpdatePasswordFailed: (state: AuthSliceState, action: PayloadAction<string>) => {
            state.updatePasswordPageIsLoading = false;
            state.updatePasswordPageError = action.payload;
        },
        setUpdatePasswordTokenCompleted: (state: AuthSliceState) => {
            state.updatePasswordPageIsLoading = false;
            state.updatePasswordPageError = undefined;
        },
        setUpdatePasswordCompleted: (state: AuthSliceState) => {
            state.updatePasswordPageIsLoading = false;
            state.updatePasswordPageError = undefined;
            state.updatePasswordApproved = true;
        },
    },
});

export const Reducer = authSlice.reducer;
export const Actions = authSlice.actions;
